<template>
  <BContainer
    class="px-0"
    fluid
  >
    <BRow
      class="py-3"
    >
      <BCol
        cols="12"
        md="6"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="gridTasks.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Activity"
          label-for="filterActivity"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="gridTasks.selected.activity"
            placeholder="filter activity here"
            input-id="filterActivity"
            label="activity_name"
            class="border border-light rounded-lg"
            clearable
            append-to-body
            :options="(gridTasks.options.activities)"
            :loading="(gridTasks.fetching.activities)"
            :disabled="(gridTasks.busy || gridTasks.fetching.activities)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterActivities"
          >
            <template #no-options="">
              no available activity
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Month"
          label-for="filterMonth"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <flat-pickr
            id="filterMonth"
            v-model="gridTasks.filter.month"
            class="form-control"
            placeholder="--------- ----"
            :config="flatPickerConfig"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="6"
        class="d-flex gap-2"
      >
        <BButton
          size="sm"
          variant="primary"
          @click.prevent="createTask"
        >
          Create Task
        </BButton>
        <BButton
          size="sm"
          variant="secondary"
          @click.prevent="importTasks"
        >
          Import Officer Tasks
        </BButton>
        <BButton
          size="sm"
          variant="dark"
          :disabled="state.downloading"
          @click.prevent="downloadTemplate"
        >
          <i
            v-if="state.downloading"
            class="fat fa-spinner fa-spin m--0 px-0"
          />
          {{ state.downloading ? 'Downloading ' : 'Download' }} Excel Template
        </BButton>
      </BCol>
    </BRow>

    <BRow
      v-if="gridTasks.busy"
      class="gap-y-6 mb-3"
    >
      <BCol
        cols="12"
      >
        <div class="text-center py-5">
          <BIcon
            icon="stopwatch"
            font-scale="5"
            animation="cylon"
          />
          <p class="h3 py-2">
            <strong>Loading . . .</strong>
          </p>
        </div>
      </BCol>
    </BRow>

    <BRow
      v-else-if="Number(gridTasks.totalRows) <= 0 && !gridTasks.busy"
    >
      <BCol
        cols="12"
        class="text-center py-5"
      >
        <BIcon
          icon="calendar"
          font-scale="5"
        />
        <p class="h3 py-2">
          <strong>No tasks found</strong>
        </p>
      </BCol>
    </BRow>

    <BRow
      v-else
      class="gap-y-6"
    >
      <BCol
        v-for="task in gridTasks.rows"
        :key="task?.id"
        cols="12"
        md="4"
      >
        <Task
          :activity="{
            activity_name: task.activity?.activity_name,
            total_buyers: task?.customers_bought_count
          }"
          :current-progress="{
            event: task.events_count,
            customer: task.customers_attended_count,
            hectare: task.total_actual_hectares,
            cost: task.total_actual_costs,
            sales: task.total_actual_sales
          }"
          :total-progress="{
            event: task.event_plan,
            customer: task.total_customer_plan,
            hectare: task.total_hectare_plan,
            cost: task.total_budget_plan,
            sales: task.total_sales_plan
          }"
        >
          <template #footer>
            <div class="d-flex justify-content-between gap-2 mt-2">
              <div class="d-flex gap-2">
                <BButton
                  size="sm"
                  variant="primary"
                  @click.prevent="editTask(task)"
                >
                  Edit
                </BButton>
                <BButton
                  v-if="Number(task.events_count) <= 0 && Number(task.customers_attended_count) <= 0"
                  size="sm"
                  variant="outline-danger"
                  @click.prevent="deleteTask(task)"
                >
                  Delete
                </BButton>
              </div>
              <BButton
                size="sm"
                variant="outline-dark"
                :to="{
                  name: 'asm.tracking.officer-task-events',
                  params: {
                    officer: officer?.id || '',
                    task: task.id
                  }
                }"
              >
                View Details
                <i class="ml-1 far fa-arrow-right" />
              </BButton>
            </div>
          </template>
        </Task>
      </BCol>
    </BRow>

    <BModal
      ref="modalCreateTask"
      title="Task"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
    >
      <ValidationObserver
        ref="formTask"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="schedule"
                name="schedule"
                rules="required"
              >
                <BFormGroup
                  label="Schedule"
                  label-for="fSchedule"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <flat-pickr
                    id="fSchedule"
                    v-model="formTask.schedule"
                    placeholder="--------- ----"
                    :class="['form-control', { 'is-invalid': errors.length > 0 }]"
                    :config="{
                      ...flatPickerConfig,
                      minDate: currentMonth,
                      allowAlt: false
                    }"
                    :disabled="state.busy || state.editing"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="activity_id"
                name="activity"
              >
                <BFormGroup
                  label="Activity"
                  label-for="fActivity"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.activity"
                    input-id="fActivity"
                    label="activity_name"
                    append-to-body
                    placeholder="select activity here"
                    :options="options.activities"
                    :disabled="fetching.activities || state.busy"
                    :loading="fetching.activities"
                    :class="{ 'is-invalid': errors.length > 0 }"
                    @search="searchActivities"
                  >
                    <template #option="{ activity_name }">
                      <div
                        class="text-break text-wrap"
                        v-text="activity_name"
                      />
                    </template>
                    <template #selected-option="{ activity_name }">
                      <div
                        class="w-225px w-md-325px text-truncate"
                        v-text="activity_name"
                      />
                    </template>
                    <template #no-options="">
                      no available activity
                    </template>
                  </VSelect>
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="event_plan"
                name="event plan"
                rules="required"
              >
                <BFormGroup
                  label="Number of Events"
                  label-for="fEventPlan"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fEventPlan"
                    v-model="formTask.event_plan"
                    type="number"
                    placeholder="0"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>
      <template #modal-footer="{ cancel }">
        <div class="w-100 d-flex justify-content-end align-items-center gap-2">
          <BButton
            variant="primary"
            :disabled="state.busy"
            @click.prevent="validateTask"
          >
            {{ state.editing ? 'Update' : 'Create' }} Task
          </BButton>
          <BButton

            variant="outline-dark"
            :disabled="state.busy"
            @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
          >
            Discard
          </BButton>
        </div>
      </template>
    </BModal>

    <BModal
      ref="modalImportTask"
      size="xl"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      modal-class="full-screen modal-no-padding"
      aria-hidden="false"
      role="dialog"
    >
      <div
        class="h-100"
        style="overflow-y: scroll; overflow-x: hidden;"
      >
        <ValidationObserver
          ref="formImportTask"
        >
          <form
            role="form"
            novalidate
            @submit.prevent
          >
            <BRow>
              <BCol
                cols="12"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="attachments"
                  name="attachments"
                  rules="required"
                >
                  <BFormGroup
                    label="Attachments"
                    label-for="fAttachments"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormFile
                      id="fAttachments"
                      v-model="formFile.attachments"
                      accept=".xls,.xlsx"
                      :state="errors.length > 0 ? false : null"
                      :disabled="state.busy"
                      @change="validateExcelFile"
                    />
                    <div
                      v-if="errors.length > 0"
                      class="invalid-feedback"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>
              <BCol
                cols="12"
                md="6"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="schedule"
                  name="schedule"
                  rules="required"
                >
                  <BFormGroup
                    label="Activity Month"
                    label-for="fActivityMonth"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fActivityMonth"
                      v-model="formImportTask.schedule"
                      type="month"
                      :state="errors.length > 0 ? false : null"
                      :disabled="state.busy"
                      readonly
                    />
                    <div
                      v-if="(errors.length > 0)"
                      class="invalid-feedback"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>
              <BCol
                cols="12"
                md="6"
              >
                <ValidationProvider
                  v-slot="{ errors }"
                  vid="sso_id"
                  name="employee id"
                  :rules="`required|is:${officer?.sso_id || ''}`"
                >
                  <BFormGroup
                    label="Employee ID"
                    label-for="fEmployeeId"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fEmployeeId"
                      v-model="formImportTask.sso_id"
                      type="text"
                      placeholder="no employee id"
                      :state="errors.length > 0 ? false : null"
                      :disabled="state.busy"
                      readonly
                    />
                    <div
                      v-if="(errors.length > 0)"
                      class="invalid-feedback"
                      v-text="errors[0]"
                    />
                  </BFormGroup>
                </ValidationProvider>
              </BCol>
            </BRow>
          </form>
        </ValidationObserver>
        <BTable
          small
          hover
          bordered
          responsive
          show-empty
          class="min-h-275px"
          :stacked="tableImportedTasks.stacked"
          :items="tableImportedTasks.rows"
          :fields="tableImportedTasks.fields"
        >
          <template #cell(index)="{ index }">
            {{ Number(index) + 1 }}
          </template>
        </BTable>
      </div>
      <template #modal-footer="{ cancel }">
        <BButton
          variant="secondary"
          :disabled="state.busy"
          @click.prevent="validateImportTask"
        >
          Import Officer Tasks
        </BButton>
        <BButton

          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>

  </BContainer>
</template>
<script>
import MOMENT from 'moment'
import QUERY from 'lodash'
import FPMonthSelect from 'flatpickr/dist/plugins/monthSelect'
import { utils, writeFile } from 'data-validation-xlsx'
import { setTableOptions, getTableOptions } from '@/forage/asm/officers/tasks'

import Task from '@/views/asm/officers/tasks/components/Task.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MActivities from '@/mixins/maintenances/activities'
import MFSOfficers from '@/mixins/accounts/fsos'

import { SASMFsoTask } from '@/services/asm'

export default {
  name: 'ASMOfficerTasks',

  components: {
    Task
  },

  mixins: [
    MxMisc,
    MxFormatter,
    MActivities,
    MFSOfficers
  ],

  beforeRouteEnter (_to, _from, navigate) {
    getTableOptions().then(options => {
      navigate(context => {
        context.state.settings = true

        if (options) {
          const { currentPage, perPage, sortBy, sortDesc, totalRows, filter } = options

          context.gridTasks = Object.assign(context.gridTasks, {
            totalRows: totalRows || 0,
            sortBy: sortBy || 'id',
            sortDesc: sortDesc || false,
            perPage: perPage || 10,
            currentPage: currentPage || 0,
            filter: {
              search: filter?.search || '',
              month: filter?.month || '',
              activity: ''
            },
            editable: {
              activity: filter?.activity || ''
            }
          })
        } else {
          context.gridTasks.currentPage = 1
        }

        context.$nextTick(() => {
          context.getActivities(context.gridTasks).finally(() => {
            if (context.gridTasks.editable.activity) {
              context.gridTasks.selected.activity = QUERY.find(context.gridTasks.options.activities, {
                id: context.gridTasks.editable.activity
              }) || ''

              context.gridTasks.editable.activity = ''
            }

            context.state.settings = false
            context.gridProvider()
          })
        })
      })
    })
  },

  props: {
    officer: {
      type: Object,
      required: true,
      default: () => ({
        id: '',
        role_id: '',
        username: '',
        first_name: '',
        last_name: '',
        mobile_number: '',
        address: '',
        sso_id: '',
        email: '',
        notification_email: '',
        provinces: [],
        photos: [],
        active: 1
      })
    }
  },

  data () {
    const currentMonth = MOMENT().format('YYYY-MM')

    return {
      currentMonth,
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        plugins: [
          new FPMonthSelect({
            dateFormat: 'Y-m',
            altFormat: 'F Y'
          })
        ]
      },
      state: {
        busy: false,
        editing: false,
        downloading: false,
        settings: false
      },
      formFile: {
        attachments: null
      },
      selected: {
        role: 2,
        activityType: 'Event'
      },
      formTask: {
        id: '',
        assignee_id: this.officer?.id || '',
        activity_id: '',
        schedule: '',
        event_plan: '',
        active: 1
      },
      gridTasks: {
        busy: false,
        fetching: {
          activities: false
        },
        options: {
          activities: []
        },
        selected: {
          role: 2,
          activity: '',
          activityType: 'Event'
        },
        editable: {
          activity: ''
        },
        filter: {
          search: '',
          month: MOMENT().format('YYYY-MM'),
          activity: ''
        },
        filterOn: [],
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: []
      },
      formImportTask: {
        assignee_id: '',
        sso_id: '',
        schedule: '',
        file_name: '',
        tasks: []
      },
      tableImportedTasks: {
        stacked: 'sm',
        fields: [
          { key: 'index', label: '#' },
          { key: 'officer_id' },
          { key: 'officer_name', label: 'Officer Name', class: 'min-w-275px' },
          { key: 'activity_name', class: 'min-w-325px' },
          { key: 'event_plan', label: 'Number of Events', class: 'text-right' },
          {
            key: 'error',
            label: 'Line Error',
            class: 'text-danger',
            formatter: row => row?.error ? `${String(row?.column || '').toLowerCase()} ${row?.error || ''}` : ''
          }
        ],
        rows: [],
        errors: []
      }
    }
  },

  watch: {

    'selected.fsOfficer' (fsOfficer) {
      this.formTask.officer_id = fsOfficer?.id || ''
    },

    'selected.activity' (activity) {
      this.formTask.activity_id = activity?.id || ''
    },

    'gridTasks.selected.activity' (activity) {
      this.gridTasks.filter.activity = activity?.id || ''
    },

    'gridTasks.filter': {
      handler () {
        if (!this.state.settings) {
          this.gridProvider()
        }
      },
      deep: true
    }
  },

  methods: {

    async gridProvider () {
      this.gridTasks.busy = true

      const payload = {
        page: this.gridTasks.currentPage,
        per_page: this.gridTasks.perPage,
        filter_text: this.gridTasks.filter.search,
        schedule: this.gridTasks.filter.month,
        assignee_id: this.officer.id,
        display: 'individual',
        activity_id: this.gridTasks.filter.activity
      }

      return SASMFsoTask.get(payload).then(
        ({ data }) => {
          const totalRows = Array.from(data || []).length
          this.gridTasks.totalRows = totalRows
          this.gridTasks.rows = data

          setTableOptions({
            currentPage: this.gridTasks.currentPage,
            perPage: this.gridTasks.perPage,
            sortBy: this.gridTasks.sortBy,
            sortDesc: this.gridTasks.sortDesc,
            totalRows: totalRows,
            filter: this.gridTasks.filter
          })
          return data
        }
      ).catch(() => {
        this.gridTasks.totalRows = 1
        setTableOptions({
          currentPage: this.gridTasks.currentPage,
          perPage: this.gridTasks.perPage,
          sortBy: this.gridTasks.sortBy,
          sortDesc: this.gridTasks.sortDesc,
          totalRows: 0,
          filter: this.gridTasks.filter
        })
        return []
      }).finally(() => {
        this.gridTasks.busy = false
      })
    },

    async searchFilterActivities (search, loading) {
      this.searchActivitiesProvider(this.gridTasks, search, loading)
    },

    createTask () {
      this.state.editing = false

      const schedule = MOMENT().format('YYYY-MM')
      this.currentMonth = schedule

      this.formTask = Object.assign(this.formTask, {
        id: '',
        assignee_id: this.officer?.id || '',
        activity_id: '',
        event_plan: '',
        schedule: schedule,
        active: 1
      })

      this.getActivities(this)

      this.$refs.modalCreateTask.show()
    },

    editTask (task) {
      this.state.editing = true

      this.formTask = Object.assign(this.formTask, {
        id: task.id,
        assignee_id: task.assignee_id,
        activity_id: task.activity_id,
        schedule: task.schedule,
        event_plan: task.event_plan,
        active: task.active
      })

      this.getActivities(this).finally(() => {
        this.selected.activity = QUERY.find(this.options.activities, {
          id: task.activity_id
        }) || ''
      })

      this.$refs.modalCreateTask.show()
    },

    async validateTask () {
      await this.$refs.formTask.validate().then(
        async validTask => {
          if (!validTask) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Task?</h6>' : '<h6>Create this Task?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putTask() : this.postTask()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    this.$refs.formTask.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    deleteTask (task) {
      return this.swalConfirm({
        html: `<div>
          <h5>Delete this Task?</h5>
          <b class="text-danger">This action cannot be undone.</b>
        </div>`,
        confirmButtonText: 'Delete',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: async () => {
          const payload = {
            id: task.id,
            remarks: ''
          }

          return SASMFsoTask.delete(payload).then(
            ({ data }) => {
              this.swalSuccess(data.message).then(() => {
                this.gridProvider()
              })
              return data
            }
          )
        }
      })
    },

    async postTask () {
      return SASMFsoTask.post(this.formTask).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalCreateTask.hide()
            this.gridProvider()
          })
          return data
        }
      )
    },

    async putTask () {
      return SASMFsoTask.put(this.formTask).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalCreateTask.hide()
            this.gridProvider()
          })
          return data
        }
      )
    },

    async downloadTemplate () {
      this.state.downloading = true

      this.getActivities(this).finally(() => {
        const month = MOMENT(this.gridTasks.filter.month).format('YYYY-MM')
        const shortFirstName = String(this.officer?.first_name || '').split(' ')
        const first = shortFirstName.length >= 1 ? String(shortFirstName[0]).substring(0, 1) : ''
        const second = shortFirstName.length > 1 ? String(shortFirstName[1]).substring(0, 1) : ''
        const shortName = String(`${first}${second}.${this.officer?.last_name}`).toUpperCase()
        const sheetProtection = {
          password: this.generateId(8), // optional
          selectLockedCells: true,
          selectUnlockedCells: true,
          formatCells: false,
          formatColumns: false,
          formatRows: false,
          insertColumns: false,
          insertRows: false,
          insertHyperlinks: false,
          deleteColumns: false,
          deleteRows: false,
          sort: false,
          autoFilter: false,
          pivotTables: false,
          objects: false,
          scenarios: false
        }

        const book = utils.book_new()

        const activitySheetData = [
          [
            'Activity Name'
          ],
          ...this.options.activities.map(
            activity => [
              activity.activity_name
            ]
          )
        ]

        const activitySheet = utils.aoa_to_sheet(activitySheetData)

        const activityLength = this.options.activities.length

        activitySheet['!cols'] = [
          { wch: 50 } // Activity Name
        ]

        activitySheet['!protect'] = sheetProtection

        const officerTaskData = [
          [
            'Officer ID', // A
            'Officer Name', // B
            'Activity Name', // C
            'Number of Events' // E
          ],
          ...Array.from({ length: this.options.activities.length }).map(
            () => [
              this.officer.sso_id,
              `${this.officer.first_name} ${this.officer.last_name}`,
              '',
              0 // Number of Events
            ]
          )
        ]

        const officerTask = utils.aoa_to_sheet(officerTaskData)

        officerTask['!cols'] = [
          { wch: 15 }, // Officer ID
          { wch: 40 }, // Officer Name
          { wch: 50 }, // Activity Name
          { wch: 20 } // Number of Events
        ]

        officerTask['!dataValidation'] = [
          {
            type: 'list-in-sheet',
            values: `=Activities!A$2:A$${activityLength + 1}`,
            sqref: 'C2:C99'
          }
        ]

        const sheetName = `${this.officer.sso_id}-${shortName}`
        utils.book_append_sheet(book, officerTask, sheetName)
        utils.book_append_sheet(book, activitySheet, 'Activities')

        const excelName = `${month}-${this.officer.sso_id}.xlsx`
        writeFile(book, excelName, { bookType: 'xlsx', type: 'binary' })

        this.state.downloading = false
      })
    },

    importTasks () {
      this.tableImportedTasks.rows = []
      this.tableImportedTasks.errors = []

      this.formImportTask = Object.assign(this.formImportTask, {
        assignee_id: this.officer?.id || '',
        sso_id: '',
        schedule: '',
        file_name: '',
        tasks: []
      })

      this.getActivities(this)

      this.$refs.modalImportTask.show()
    },

    async validateFormFile (file) {
      return new Promise((resolve, reject) => {
        const extractPayloads = String(file.name).replace('.xlsx', '').replace('.xls', '').split('-')

        const validations = {
          swal: '<h6>File name should be in the format: <br /><b>YYYY-MM-EmployeeID</b> <br /><br />Example: 2025-01-10001.xlsx</h6>',
          input: 'File name should be in the format: YYYY-MM-EmployeeID. Example: 2025-01-10001.xlsx'
        }

        if (extractPayloads.length <= 2) {
          return reject(validations)
        }

        const year = extractPayloads[0]
        const month = extractPayloads[1]
        const employeeID = String(extractPayloads[2])

        const dateFormat = 'YYYY-MM'
        const originalDate = `${year}-${month}`

        const formattedDate = MOMENT(originalDate, dateFormat, true)

        if (!formattedDate.isValid() && employeeID) {
          return reject(validations)
        }

        resolve({
          schedule: formattedDate.format(dateFormat),
          employeeID,
          fileName: file.name
        })
      })
    },

    async validateExcelFile ({ target }) {
      this.formImportTask.tasks = []
      this.tableImportedTasks.rows = []
      this.tableImportedTasks.errors = []

      const file = QUERY.first(target.files)

      if (file) {
        this.validateFormFile(file).then(
          ({ employeeID, schedule, fileName }) => {
            this.validateImportedTask(file, this.options.activities).then(
              ({ rows, errors }) => {
                this.tableImportedTasks.errors = errors
                this.tableImportedTasks.rows = rows

                this.formImportTask = Object.assign(this.formImportTask, {
                  schedule,
                  sso_id: employeeID,
                  file_name: fileName,
                  tasks: rows.map(
                    row => Object.assign(row, {
                      schedule
                    })
                  )
                })
              }
            )
          }
        ).catch(
          ({ swal, input }) => {
            this.swalInvalid(swal, 'Invalid File Name Format').then(() => {
              this.$refs.formImportTask.setErrors({
                attachments: [input]
              })
            })
          }
        )
      }
    },

    validateImportTask () {
      this.$refs.formImportTask.validate().then(
        validTask => {
          if (!validTask) {
            return this.swalInvalid()
          }

          const file = this.formFile.attachments
          if (file) {
            this.validateFormFile(file).then(() => {
              const invalidExcel = this.tableImportedTasks.errors.length > 0
              const invalidCards = this.formImportTask.tasks.length <= 0

              const currentMonth = MOMENT()
              const currentSchedule = MOMENT(this.formImportTask.schedule).isSameOrAfter(currentMonth, 'month')

              if (invalidExcel || invalidCards || !currentSchedule) {
                if (!currentSchedule) {
                  this.$refs.formImportTask.setErrors({
                    schedule: [
                      `The activity month must be greater than or equal to ${currentMonth.format('MMMM YYYY')}`
                    ]
                  })
                }

                return this.swalInvalid()
              }
              this.swalConfirm({
                html: 'Import Officer Tasks?',
                confirmButtonText: 'Import',
                customClass: {
                  confirmButton: 'btn btn-sm btn-secondary'
                },
                preConfirm: () => {
                  return this.putImportTask().catch(
                    ({ message }) => {
                      if (message && message.errors) {
                        this.$refs.formImportTask.setErrors({
                          attachments: [message?.message]
                        })

                        this.$refs.formImportTask.setErrors(message.errors)
                      }
                    }
                  )
                }
              })
            }).catch(
              ({ swal, input }) => {
                this.swalInvalid(swal, 'Invalid File Name Format').then(
                  () => {
                    this.$refs.formImportTask.setErrors({
                      attachments: [input]
                    })
                  }
                )
              }
            )
          }
        }
      )
    },

    async putImportTask () {
      return SASMFsoTask.singleImport(this.formImportTask).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalImportTask.hide()
            this.gridProvider()
          })
          return data
        }
      )
    }
  }
}
</script>
